import { Button, Dialog, notify, Select, Toggle } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { HOUR_OPTIONS, MONTHDAY_OPTIONS, ROLE_LABEL, WEEKDAY_OPTIONS, type Recipient, type SavedView, type Schedule, type SchedulesResponse } from "./reportsApi";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
type Freq = Schedule["frequency"];

/**
 * "Email this report" for one saved view: daily, weekly or monthly at an Eastern hour, to console staff picked
 * from the staff list (customers can't be added). CSV attached, or just a link back to the view.
 */
export function ScheduleDialog({ view, open, onClose }: { view: SavedView | null; open: boolean; onClose: () => void }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const [frequency, setFrequency] = useState<Freq>("weekly");
  const [hour, setHour] = useState("7");
  const [weekday, setWeekday] = useState("1");
  const [monthDay, setMonthDay] = useState("1");
  const [delivery, setDelivery] = useState<"csv" | "link">("csv");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const recipients = useQuery({ queryKey: ["console", "report-recipients"], queryFn: () => api<{ recipients: Recipient[] }>("/v1/console/report-recipients"), enabled: open });
  const schedules = useQuery({ queryKey: ["console", "report-schedules"], queryFn: () => api<SchedulesResponse>("/v1/console/report-schedules"), enabled: open });
  useEffect(() => {
    if (open) {
      setPicked(new Set([me.user.email.toLowerCase()]));
      setError(null);
    }
  }, [open, me.user.email]);
  const create = useMutation({
    mutationFn: () =>
      api<{ schedule: Schedule }>(`/v1/console/report-views/${view!.id}/schedules`, {
        method: "POST",
        body: { frequency, hour: Number(hour), weekday: frequency === "weekly" ? Number(weekday) : null, monthDay: frequency === "monthly" ? Number(monthDay) : null, delivery, recipients: [...picked] },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "report-schedules"] });
      void qc.invalidateQueries({ queryKey: ["console", "report-views"] });
      notify.success("Schedule saved", r.schedule.state === "waiting_for_email" ? "Nothing is sent until email is connected." : `${r.schedule.recipients.length} recipient${r.schedule.recipients.length === 1 ? "" : "s"}.`);
      onClose();
    },
    onError: (e) => setError(message(e)),
  });
  const list = recipients.data?.recipients ?? [];
  const costHidden = list.some((r) => picked.has(r.email) && !r.seesCost);
  const waiting = schedules.data && !schedules.data.emailConnected;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Email this report"
      description={view ? `"${view.name}" (${view.reportTitle}). Times are Eastern. Only console staff can receive it.` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!view || picked.size === 0} onClick={() => create.mutate()}>Save schedule</Button>
        </>
      }
    >
      <div className="ffa-form ffa-schedform">
        {waiting ? (
          <div className="ffa-notice ffa-notice--compact" role="status">
            <span>Waiting for email to be connected. The schedule is saved but nothing is sent until customer email (Resend) is connected in <Link to="/admin/integrations">Integrations</Link>.</span>
          </div>
        ) : null}
        <div className="ffa-schedform__row">
          <Select label="How often" value={frequency} onChange={(v) => setFrequency(v as Freq)} options={[{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }]} />
          {frequency === "weekly" ? <Select label="Day" value={weekday} onChange={setWeekday} options={WEEKDAY_OPTIONS} /> : null}
          {frequency === "monthly" ? <Select label="Day of month" value={monthDay} onChange={setMonthDay} options={MONTHDAY_OPTIONS} /> : null}
          <Select label="Time" value={hour} onChange={setHour} options={HOUR_OPTIONS} />
        </div>
        <Select label="Send" value={delivery} onChange={(v) => setDelivery(v as "csv" | "link")} options={[{ value: "csv", label: "CSV attachment and a link" }, { value: "link", label: "Link to the saved view only" }]} />
        <fieldset className="ffa-recipients">
          <legend className="ffx-micro">Recipients (console staff)</legend>
          {recipients.isLoading ? <p className="ffa-muted ffa-small">Loading staff…</p> : null}
          <ul>
            {list.map((r) => (
              <li key={r.email}>
                <Toggle
                  label={r.name || r.email}
                  description={`${r.email} · ${ROLE_LABEL[r.role]}`}
                  checked={picked.has(r.email)}
                  onChange={(on) => setPicked((s) => { const n = new Set(s); if (on) n.add(r.email); else n.delete(r.email); return n; })}
                />
              </li>
            ))}
          </ul>
        </fieldset>
        {costHidden ? <p className="ffa-small ffa-muted">Cost, margin and markup are left out of the email because a recipient&apos;s role can&apos;t see them.</p> : null}
        {error ? <p className="ffa-error" role="alert">{error}</p> : null}
      </div>
    </Dialog>
  );
}
